import { ComponentType } from 'react';

import {
  AccountSettingsPage,
  ActionBuilderDetailPage,
  ActionBuilderPage,
  AgentDetailPage,
  AgentQA,
  AgentQaSetupPage,
  AiQaPage,
  AssistConfigurationPage,
  AssistInsightsAgentDetailPage,
  AssistInsightsPage,
  AssistNotesPage,
  AssistPreviewPage,
  AutonomousAgentPreview,
  BuilderParentPage,
  DiscoverAutomationPage,
  DiscoverInsightsPage,
  EmailPreviewPage,
  EventsPage,
  HomePage,
  IntegrationsMain,
  IntentTestDetailPage,
  KnowledgePage,
  LookerDashboardPage,
  OrchestratorDetailPage,
  OrchestratorLandingPage,
  OrchestratorTestPage,
  ParentLandingPage,
  PIIRedactionSettingsPage,
  PredictIntentsPage,
  SolveAgentTestingPage,
  SolveAnalyticsPageV2,
  SolveConfigurationPage,
  SolveInsightsArticleDetail,
  SolveInsightsConversationRedirect,
  SolveInsightsInsightCard,
  SolveInsightsInsightCardView,
  SolveInsightsMain,
  SolveInsightsTopicDetail,
  SolveInsightsWorkflowDetail,
  SolvePreviewPage,
  SSOSettingsPage,
  SubscriptionPlanPage,
  TeamSettingsPage,
  TopicDetailPage,
  TriageConfigDetailPage,
  TriageInsightsPage,
  TriageModelDetailPage,
  TriageModelsOverviewPage,
  TriageSimulationsDetailPage,
  TriageSimulationsPage,
  TriageVersionControlDetail,
  WorkflowPreviewPage,
} from './lazyRoutes';
import {
  ROUTE_ACCOUNT_SETTINGS,
  ROUTE_ACTION_BUILDER,
  ROUTE_ACTION_BUILDER_EDIT_V2,
  ROUTE_AGENT_AB_TEST,
  ROUTE_AGENT_AB_TEST_DETAIL,
  ROUTE_AGENT_INSIGHT_DETAIL,
  ROUTE_AGENT_QA,
  ROUTE_AGENT_QA_INSIGHTS,
  ROUTE_AGENT_QA_SETUP,
  ROUTE_AI_QA,
  ROUTE_ASSIST_CONFIGURATION,
  ROUTE_ASSIST_INSIGHTS,
  ROUTE_ASSIST_INSIGHTS_AGENT_DETAIL,
  ROUTE_ASSIST_INSIGHTS_INSIGHT_CARD,
  ROUTE_ASSIST_INSIGHTS_INSIGHT_CARD_VIEW,
  ROUTE_ASSIST_NOTES,
  ROUTE_ASSIST_PREVIEW,
  ROUTE_ASSIST_WORKFLOW_BUILDER,
  ROUTE_AUTONOMOUS_AGENT_PREVIEW,
  ROUTE_DISCOVER_AUTOMATION,
  ROUTE_DISCOVER_INSIGHTS,
  ROUTE_INTEGRATIONS,
  ROUTE_KNOWLEDGE,
  ROUTE_LOG,
  ROUTE_LOOKER_ANALYTICS,
  ROUTE_MACRO_CONTROLS,
  ROUTE_ORCHESTRATOR,
  ROUTE_ORCHESTRATOR_DETAIL,
  ROUTE_ORCHESTRATOR_TEST,
  ROUTE_PLAN_PAGE,
  ROUTE_PREDICT_INTENTS,
  ROUTE_PREDICT_INTENTS_TEST_DETAIL,
  ROUTE_PREDICTION_SETTINGS,
  ROUTE_PREDICTION_SETTINGS_DETAIL,
  ROUTE_SECURITY,
  ROUTE_SOLVE,
  ROUTE_SOLVE_CONFIGURATION,
  ROUTE_SOLVE_INSIGHTS_ARTICLE_DETAIL,
  ROUTE_SOLVE_INSIGHTS_CONVERSATION_DETAIL,
  ROUTE_SOLVE_INSIGHTS_INSIGHT_CARD,
  ROUTE_SOLVE_INSIGHTS_INSIGHT_CARD_VIEW,
  ROUTE_SOLVE_INSIGHTS_MAIN,
  ROUTE_SOLVE_INSIGHTS_TOPIC_DETAIL,
  ROUTE_SOLVE_INSIGHTS_TOPIC_DETAIL_PARENT,
  ROUTE_SOLVE_INSIGHTS_WORKFLOW_DETAIL,
  ROUTE_SOLVE_LITE,
  ROUTE_SOLVE_PREVIEW,
  ROUTE_SOLVE_TESTS,
  ROUTE_SSO_SETTINGS,
  ROUTE_TEAM,
  ROUTE_TOPIC_DETAIL,
  ROUTE_TRIAGE_INSIGHTS,
  ROUTE_TRIAGE_MODELS,
  ROUTE_TRIAGE_SIMULATIONS,
  ROUTE_TRIAGE_SIMULATIONS_DETAIL,
  ROUTE_TRIAGE_VERSION_CONTROL,
  ROUTE_WORKFLOW_BUILDER,
  ROUTE_WORKFLOW_BUILDER_EDIT,
  ROUTE_WORKFLOW_BUILDER_EDIT_EMAIL_PREVIEW,
  ROUTE_WORKFLOW_BUILDER_PREVIEW,
} from './routeDefinitions';
import AgentAbTestDetailPage from 'src/pages/agent-ab-test/AgentAbTestDetailPage';
import AgentAbTestPage from 'src/pages/agent-ab-test/AgentAbTestPage';
import { Routes } from 'src/utils/enums';

export interface RouteConfig {
  component: ComponentType<React.PropsWithChildren<any>>;
  featureFlags?: string[];
  ignorePermissionForSuperAdmin?: boolean;
  path: `${Routes}`;
  permissionKey?: string;
  requiredTiers?: ReadonlyArray<'pro' | 'base' | 'enterprise'>;
  roles?: Set<string>;
  showNavBar?: boolean;
  showSideBar?: boolean;
}

// When creating the routes, ORDER MATTERS
const routes: RouteConfig[] = [
  {
    component: HomePage,
    path: Routes.HOMEPAGE,
  },
  {
    ...ROUTE_DISCOVER_INSIGHTS,
    component: DiscoverInsightsPage,
  },
  {
    ...ROUTE_DISCOVER_AUTOMATION,
    component: DiscoverAutomationPage,
  },
  {
    ...ROUTE_TOPIC_DETAIL,
    component: TopicDetailPage,
  },
  { ...ROUTE_ASSIST_NOTES, component: AssistNotesPage },
  {
    ...ROUTE_ASSIST_CONFIGURATION,
    component: AssistConfigurationPage,
  },
  {
    ...ROUTE_ASSIST_INSIGHTS_AGENT_DETAIL,
    component: AssistInsightsAgentDetailPage,
    showNavBar: false,
  },
  {
    ...ROUTE_ASSIST_INSIGHTS,
    component: AssistInsightsPage,
  },
  {
    ...ROUTE_ASSIST_PREVIEW,
    component: AssistPreviewPage,
    showNavBar: false,
  },
  {
    ...ROUTE_SOLVE,
    component: SolveAnalyticsPageV2,
  },
  {
    ...ROUTE_SOLVE_LITE,
    component: SolveConfigurationPage,
  },
  {
    ...ROUTE_SOLVE_PREVIEW,
    component: SolvePreviewPage,
  },
  {
    ...ROUTE_MACRO_CONTROLS,
    component: SolveAnalyticsPageV2,
  },
  {
    ...ROUTE_WORKFLOW_BUILDER,
    component: ParentLandingPage,
  },
  { ...ROUTE_ASSIST_WORKFLOW_BUILDER, component: ParentLandingPage },
  {
    ...ROUTE_PREDICT_INTENTS,
    component: PredictIntentsPage,
  },
  {
    ...ROUTE_PREDICT_INTENTS_TEST_DETAIL,
    component: IntentTestDetailPage,
    showNavBar: false,
  },
  {
    ...ROUTE_SOLVE_CONFIGURATION,
    component: SolveConfigurationPage,
  },
  {
    ...ROUTE_AGENT_AB_TEST,
    component: AgentAbTestPage,
  },
  {
    ...ROUTE_AGENT_AB_TEST_DETAIL,
    component: AgentAbTestDetailPage,
    showNavBar: false,
  },
  {
    ...ROUTE_AUTONOMOUS_AGENT_PREVIEW,
    component: AutonomousAgentPreview,
    showNavBar: false,
  },
  {
    ...ROUTE_WORKFLOW_BUILDER_PREVIEW,
    component: WorkflowPreviewPage,
    showNavBar: false,
  },
  {
    ...ROUTE_WORKFLOW_BUILDER_EDIT,
    component: BuilderParentPage,
  },
  {
    ...ROUTE_WORKFLOW_BUILDER_EDIT_EMAIL_PREVIEW,
    component: EmailPreviewPage,
    showNavBar: false,
  },

  {
    ...ROUTE_ACTION_BUILDER,
    component: ActionBuilderPage,
  },
  {
    ...ROUTE_ACTION_BUILDER_EDIT_V2,
    component: ActionBuilderDetailPage,
    showNavBar: false,
  },
  {
    ...ROUTE_PREDICTION_SETTINGS,
    component: TriageModelsOverviewPage,
    showNavBar: false,
  },
  {
    ...ROUTE_PLAN_PAGE,
    component: SubscriptionPlanPage,
  },
  {
    ...ROUTE_PREDICTION_SETTINGS_DETAIL,
    component: TriageConfigDetailPage,
  },
  {
    ...ROUTE_ACCOUNT_SETTINGS,
    component: AccountSettingsPage,
  },
  {
    ...ROUTE_INTEGRATIONS,
    component: IntegrationsMain,
  },
  {
    ...ROUTE_TEAM,
    component: TeamSettingsPage,
  },
  {
    ...ROUTE_LOG,
    component: EventsPage,
  },
  {
    ...ROUTE_SSO_SETTINGS,
    component: SSOSettingsPage,
  },
  {
    ...ROUTE_SECURITY,
    component: PIIRedactionSettingsPage,
  },
  {
    ...ROUTE_LOOKER_ANALYTICS,
    component: LookerDashboardPage,
    showNavBar: false,
  },
  { ...ROUTE_AGENT_QA, component: AgentQA },
  { ...ROUTE_AGENT_QA_INSIGHTS, component: AgentQA },
  { ...ROUTE_AGENT_QA_SETUP, component: AgentQaSetupPage },
  { ...ROUTE_AI_QA, component: AiQaPage },
  {
    ...ROUTE_AGENT_INSIGHT_DETAIL,
    component: AgentDetailPage,
    showNavBar: false,
  },
  { ...ROUTE_SOLVE_INSIGHTS_MAIN, component: SolveInsightsMain },
  { ...ROUTE_KNOWLEDGE, component: KnowledgePage },
  { ...ROUTE_ORCHESTRATOR, component: OrchestratorLandingPage },
  {
    ...ROUTE_ORCHESTRATOR_TEST,
    component: OrchestratorTestPage,
    showNavBar: false,
    showSideBar: false,
  },
  { ...ROUTE_ORCHESTRATOR_DETAIL, component: OrchestratorDetailPage },
  {
    ...ROUTE_SOLVE_INSIGHTS_TOPIC_DETAIL,
    component: SolveInsightsTopicDetail,
    showNavBar: false,
  },
  {
    ...ROUTE_SOLVE_INSIGHTS_TOPIC_DETAIL_PARENT,
    component: SolveInsightsTopicDetail,
    showNavBar: false,
  },
  {
    ...ROUTE_SOLVE_INSIGHTS_ARTICLE_DETAIL,
    component: SolveInsightsArticleDetail,
    showNavBar: false,
  },
  {
    ...ROUTE_SOLVE_INSIGHTS_CONVERSATION_DETAIL,
    component: SolveInsightsConversationRedirect,
    showNavBar: false,
    showSideBar: false,
  },
  {
    ...ROUTE_TRIAGE_MODELS,
    component: TriageModelDetailPage,
    showNavBar: false,
  },
  {
    ...ROUTE_TRIAGE_VERSION_CONTROL,
    component: TriageVersionControlDetail,
    showNavBar: false,
  },
  {
    ...ROUTE_TRIAGE_SIMULATIONS,
    component: TriageSimulationsPage,
    showNavBar: false,
  },
  {
    ...ROUTE_TRIAGE_SIMULATIONS_DETAIL,
    component: TriageSimulationsDetailPage,
    showNavBar: false,
  },
  {
    ...ROUTE_SOLVE_INSIGHTS_WORKFLOW_DETAIL,
    component: SolveInsightsWorkflowDetail,
    showNavBar: false,
  },
  {
    ...ROUTE_TRIAGE_INSIGHTS,
    component: TriageInsightsPage,
  },
  {
    ...ROUTE_SOLVE_TESTS,
    component: SolveAgentTestingPage,
    showNavBar: false,
  },
  {
    ...ROUTE_SOLVE_INSIGHTS_INSIGHT_CARD_VIEW,
    component: SolveInsightsInsightCardView,
    showNavBar: false,
  },
  {
    ...ROUTE_ASSIST_INSIGHTS_INSIGHT_CARD_VIEW,
    component: SolveInsightsInsightCardView,
    showNavBar: false,
  },
  {
    ...ROUTE_SOLVE_INSIGHTS_INSIGHT_CARD,
    component: SolveInsightsInsightCard,
    showNavBar: false,
  },
  {
    ...ROUTE_ASSIST_INSIGHTS_INSIGHT_CARD,
    component: SolveInsightsInsightCard,
    showNavBar: false,
  },
];

export default routes;
